import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AutoComplete } from 'primeng/autocomplete';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil, timeout } from 'rxjs/operators';
import {
  StudentGroupSelectionItem,
  StudentGroupSelectionSection,
  StudentGroupSelectionValue
} from '../../models/student-group-selection.model';
import { StudentGroupSelectionRepositoryService } from '../../services/student-group-selection-repository.service';

@Component({
  selector: 'app-student-group-select',
  templateUrl: './student-group-select.component.html',
  styleUrls: ['./student-group-select.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => StudentGroupSelectComponent),
    multi: true
  }]
})
export class StudentGroupSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @ViewChild('autoComplete') autoComplete: AutoComplete;

  @Input() flightSchoolId: number;
  @Input() courseId: number;
  @Input() placeholder = 'Add another...';
  @Input() label = 'Assign to';
  @Input() showLabel = true;
  @Input() disabled = false;
  @Input() readOnly = false;
  @Input() inputId: string;
  @Input() studentLabelOverrides: { [studentId: number]: string } = {};
  @Input() excludedStudentIds: number[] = [];
  @Input() excludedStudentMessage = 'This student is already assigned.';
  @Input() refreshOptionsOnLoad = false;

  @Output() resolvedStudentIdsChange = new EventEmitter<number[]>();
  @Output() ignoredStudentCountChange = new EventEmitter<number>();

  value: StudentGroupSelectionValue = this.emptyValue();
  selectedItems: StudentGroupSelectionItem[] = [];
  suggestions: StudentGroupSelectionSection[] = [];
  studentTotalLabel = '0 students total';
  eligibilityWarningMessage: string;
  ineligibleStudentNames: string[] = [];
  ineligibleStudentNamesLabel: string;
  overlappingStudentNames: string[] = [];
  overlappingStudentNamesLabel: string;
  overlapWarningMessage: string;
  excludedStudentNames: string[] = [];
  excludedStudentNamesLabel: string;
  excludedStudentWarningMessage: string;
  loading = false;
  /* PrimeNG defaults the panel to 200px, which is 3.57 of our 56px option rows
     (.sg-option min-height 42 + 8 own padding + 6.86 x2 from .p-autocomplete-item),
     so the list opens on a half-cut row and a dead strip. 5 whole rows instead.
     Group headers are shorter, so a filtered list that mixes them can still end
     mid-row — that reads as "more below", which is what we want. */
  readonly autocompletePanelScrollHeight = '280px';
  readonly autocompletePanelStyle: { [key: string]: string } = { 'z-index': '1030' };

  private items: StudentGroupSelectionItem[] = [];
  private resolvedStudentIds: number[] = [];
  private resolutionVersion = 0;
  private openSuggestionsTimeout: any;
  private touched = false;
  private optionsRefreshed = false;
  private readonly destroy$ = new Subject<void>();

  private onChange: (value: StudentGroupSelectionValue) => void = () => null;
  private onTouched: () => void = () => null;

  constructor(private repository: StudentGroupSelectionRepositoryService) { }

  ngOnInit(): void {
    this.loadItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.flightSchoolId || changes.courseId) {
      this.loadItems();
      return;
    }

    if (changes.excludedStudentIds) {
      this.resolveStudentIds();
    }

    if (changes.studentLabelOverrides) {
      this.syncSelectedItems();
    }
  }

  ngOnDestroy(): void {
    if (this.openSuggestionsTimeout) {
      clearTimeout(this.openSuggestionsTimeout);
    }

    this.destroy$.next();
    this.destroy$.complete();
  }

  writeValue(value: StudentGroupSelectionValue | number[]): void {
    this.value = this.normalizeValue(value);
    this.syncSelectedItems();
    this.resolveStudentIds();
    this.updateInputPlaceholder();
  }

  registerOnChange(fn: (value: StudentGroupSelectionValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  search(event: { query: string }): void {
    this.updateSuggestions(event?.query || '');
  }

  openSuggestions(event?: Event): void {
    if (this.disabled || this.readOnly) {
      return;
    }

    this.updateSuggestions('');

    if (this.openSuggestionsTimeout) {
      clearTimeout(this.openSuggestionsTimeout);
    }

    this.openSuggestionsTimeout = setTimeout(() => {
      if (this.autoComplete) {
        this.autoComplete.show();
        this.updateInputPlaceholder();
      }
    }, 30);
  }

  private updateSuggestions(searchText: string): void {
    const query = (searchText || '').toLowerCase().trim();
    const selectedKeys = this.selectedItems.map(item => this.getItemKey(item));
    const filteredItems = this.items
      .filter(item => selectedKeys.indexOf(this.getItemKey(item)) < 0)
      .filter(item => item.isEligible !== false)
      .filter(item => !query || item.label.toLowerCase().indexOf(query) >= 0 || (item.detail || '').toLowerCase().indexOf(query) >= 0);

    this.suggestions = this.buildSections(filteredItems, query);
  }

  selectedItemsChanged(items: StudentGroupSelectionItem[]): void {
    this.selectedItems = [...(items || [])].filter(item => item.type !== 'empty');
    this.value = {
      studentIds: this.selectedItems.filter(item => item.type === 'student').map(item => item.id),
      flightSchoolGroupIds: this.selectedItems.filter(item => item.type === 'group').map(item => item.id)
    };

    this.touch();
    this.onChange(this.cloneValue(this.value));
    this.resolveStudentIds();
    this.updateInputPlaceholder();
  }

  removeItem(item: StudentGroupSelectionItem, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.readOnly) {
      return;
    }

    this.selectedItemsChanged(this.selectedItems.filter(selectedItem => this.getItemKey(selectedItem) !== this.getItemKey(item)));
  }

  trackItem(index: number, item: StudentGroupSelectionItem): string {
    return this.getItemKey(item);
  }

  trackSection(index: number, section: StudentGroupSelectionSection): string {
    return section.label;
  }

  private loadItems(): void {
    if (!this.flightSchoolId) {
      this.items = [];
      this.suggestions = [];
      this.syncSelectedItems();
      this.resolveStudentIds();
      return;
    }

    if (this.refreshOptionsOnLoad && !this.optionsRefreshed) {
      this.optionsRefreshed = true;
      this.repository.clearSelectionItems();
    }

    this.loading = true;

    this.repository.getSelectionItems(this.flightSchoolId, this.courseId)
      .pipe(
        timeout(8000),
        catchError(() => of([])),
        takeUntil(this.destroy$)
      )
      .subscribe(items => {
        this.loading = false;
        this.items = items || [];
        this.suggestions = this.buildSections(this.items.filter(item => item.isEligible !== false), '');
        this.syncSelectedItems();
        this.resolveStudentIds();
        this.updateInputPlaceholder();
      }, () => {
        this.loading = false;
        this.items = [];
        this.suggestions = this.buildSections([], '');
        this.syncSelectedItems();
        this.resolveStudentIds();
        this.updateInputPlaceholder();
      });
  }

  private syncSelectedItems(): void {
    const selectedItems: StudentGroupSelectionItem[] = [];

    for (const groupId of this.value.flightSchoolGroupIds) {
      const group = this.items.find(item => item.type === 'group' && item.id === groupId);
      selectedItems.push(group || {
        id: groupId,
        type: 'group',
        label: `Group ${groupId}`,
        studentCount: 0,
        studentIds: []
      });
    }

    for (const studentId of this.value.studentIds) {
      const student = this.items.find(item => item.type === 'student' && item.id === studentId);
      selectedItems.push(student || {
        id: studentId,
        type: 'student',
        label: this.studentLabelOverrides?.[studentId] || `Student ${studentId}`,
        initials: this.getInitials(this.studentLabelOverrides?.[studentId])
      });
    }

    this.selectedItems = selectedItems;
    this.updateInputPlaceholder();
  }

  private resolveStudentIds(): void {
    const resolutionVersion = ++this.resolutionVersion;
    const directStudentIds = (this.value.studentIds || [])
      .filter(studentId => {
        const student = this.items.find(item => item.type === 'student' && item.id === studentId);
        return (!student || student.isEligible !== false) && !this.isStudentExcluded(studentId);
      });

    if (!this.flightSchoolId || !this.value.flightSchoolGroupIds.length) {
      this.setResolvedStudentIdsIfCurrent(directStudentIds, resolutionVersion);
      return;
    }

    this.repository.getStudentIdsForGroups(this.flightSchoolId, this.value.flightSchoolGroupIds, this.courseId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(groupStudentIds => this.setResolvedStudentIdsIfCurrent([
        ...directStudentIds,
        ...(groupStudentIds || []).filter(studentId => !this.isStudentExcluded(studentId))
      ], resolutionVersion));
  }

  private setResolvedStudentIdsIfCurrent(studentIds: number[], resolutionVersion: number): void {
    if (resolutionVersion === this.resolutionVersion) {
      this.setResolvedStudentIds(studentIds);
    }
  }

  private setResolvedStudentIds(studentIds: number[]): void {
    this.resolvedStudentIds = this.distinct((studentIds || []).filter(studentId => !!studentId));
    const count = this.resolvedStudentIds.length;

    this.studentTotalLabel = `${count} ${count === 1 ? 'student' : 'students'} total`;
    this.updateEligibilityWarning();
    this.resolvedStudentIdsChange.emit([...this.resolvedStudentIds]);
    this.ignoredStudentCountChange.emit(this.ineligibleStudentNames.length);
  }

  private updateEligibilityWarning(): void {
    const names = this.selectedItems.reduce((ineligibleNames, item) => {
      if (item.type === 'student' && item.isEligible === false) {
        ineligibleNames.push(item.label);
      }

      if (item.type === 'group') {
        ineligibleNames.push(...(item.ignoredStudentNames || []));
      }

      return ineligibleNames;
    }, [] as string[]);

    this.ineligibleStudentNames = names.filter((name, index, source) => source.indexOf(name) === index);
    this.ineligibleStudentNamesLabel = this.ineligibleStudentNames.join(', ');
    const count = this.ineligibleStudentNames.length;
    this.eligibilityWarningMessage = count > 0
      ? `${count} ${count === 1 ? 'student will' : 'students will'} be ignored because they are not enrolled in this course.`
      : null;

    const directStudentIds = this.selectedItems
      .filter(item => item.type === 'student' && item.isEligible !== false && !this.isStudentExcluded(item.id))
      .map(item => item.id);
    const groupStudentIds = this.selectedItems
      .filter(item => item.type === 'group')
      .reduce((studentIds, group) => studentIds.concat(group.allStudentIds || group.studentIds || []), [] as number[]);
    const overlappingStudentIds = this.distinct(directStudentIds.filter(studentId => groupStudentIds.indexOf(studentId) >= 0));

    this.selectedItems.forEach(item => {
      item.isOverlappingSelection = item.type === 'student' && overlappingStudentIds.indexOf(item.id) >= 0;
    });

    this.overlappingStudentNames = overlappingStudentIds.map(studentId => {
      const student = this.items.find(item => item.type === 'student' && item.id === studentId);
      return student?.label || `Student ${studentId}`;
    });
    this.overlappingStudentNamesLabel = this.overlappingStudentNames.join(', ');
    const overlapCount = this.overlappingStudentNames.length;
    this.overlapWarningMessage = overlapCount > 0
      ? `${overlapCount} selected ${overlapCount === 1 ? 'student is' : 'students are'} also included through a selected group and will only be counted once.`
      : null;

    const selectedStudentIds = this.selectedItems
      .filter(item => item.type === 'student' && item.isEligible !== false)
      .map(item => item.id);
    const selectedGroupStudentIds = this.selectedItems
      .filter(item => item.type === 'group')
      .reduce((studentIds, group) => studentIds.concat(group.studentIds || []), [] as number[]);
    const excludedIds = this.distinct([...selectedStudentIds, ...selectedGroupStudentIds])
      .filter(studentId => this.isStudentExcluded(studentId));

    this.selectedItems.forEach(item => {
      item.isExcluded = item.type === 'student' && excludedIds.indexOf(item.id) >= 0;
    });
    this.excludedStudentNames = excludedIds.map(studentId => {
      const student = this.items.find(item => item.type === 'student' && item.id === studentId);
      return student?.label || `Student ${studentId}`;
    });
    this.excludedStudentNamesLabel = this.excludedStudentNames.join(', ');
    const excludedCount = this.excludedStudentNames.length;
    this.excludedStudentWarningMessage = excludedCount > 0
      ? `${excludedCount} ${excludedCount === 1 ? 'student is' : 'students are'} already on this assignment and will not be added again.`
      : null;
  }

  private isStudentExcluded(studentId: number): boolean {
    return (this.excludedStudentIds || []).indexOf(studentId) >= 0;
  }

  private buildSections(items: StudentGroupSelectionItem[], searchText: string): StudentGroupSelectionSection[] {
    const groups = items.filter(item => item.type === 'group');
    const students = items.filter(item => item.type === 'student');
    const sections: StudentGroupSelectionSection[] = [];

    if (groups.length) {
      sections.push({ label: 'Groups', items: groups });
    }

    if (students.length) {
      sections.push({ label: 'Students', items: students });
    }

    if (sections.length) {
      return sections;
    }

    return [{
      label: 'Results',
      items: [{
        id: 0,
        type: 'empty',
        label: this.loading ? 'Loading students and groups...' : (searchText ? 'No matches found' : 'No students or groups found'),
        detail: this.loading ? undefined : 'Check the selected school and course'
      }]
    }];
  }

  private updateInputPlaceholder(): void {
    setTimeout(() => {
      const input = this.autoComplete?.multiInputEL?.nativeElement || this.autoComplete?.inputEL?.nativeElement;

      if (input) {
        input.placeholder = this.placeholder;
      }
    });
  }

  private normalizeValue(value: StudentGroupSelectionValue | number[]): StudentGroupSelectionValue {
    if (Array.isArray(value)) {
      return {
        studentIds: this.distinct(value.filter(studentId => !!studentId)),
        flightSchoolGroupIds: []
      };
    }

    return {
      studentIds: this.distinct((value?.studentIds || []).filter(studentId => !!studentId)),
      flightSchoolGroupIds: this.distinct((value?.flightSchoolGroupIds || []).filter(groupId => !!groupId))
    };
  }

  private cloneValue(value: StudentGroupSelectionValue): StudentGroupSelectionValue {
    return {
      studentIds: [...(value.studentIds || [])],
      flightSchoolGroupIds: [...(value.flightSchoolGroupIds || [])]
    };
  }

  private emptyValue(): StudentGroupSelectionValue {
    return {
      studentIds: [],
      flightSchoolGroupIds: []
    };
  }

  private getItemKey(item: StudentGroupSelectionItem): string {
    return `${item.type}-${item.id}`;
  }

  private getInitials(label: string): string {
    return (label || '?').split(' ').slice(0, 2).map(part => part.charAt(0)).join('').toUpperCase();
  }

  private distinct(values: number[]): number[] {
    return values.filter((value, index, source) => source.indexOf(value) === index);
  }

  private touch(): void {
    if (this.touched) {
      return;
    }

    this.touched = true;
    this.onTouched();
  }
}
